import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';
import { useEffect, useState } from 'react';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { useCreateAor } from 'hooks/useAors';
import { AreaOfResponsibility } from '../../types/aor';
import { GeoLocation } from '../../components/GeoLocationComponent/GeoLocationComponent';

interface FormData {
  name: string;
  description: string;
}

interface AddUpdateAorProps {
  show: boolean;
  onClose: () => void;
  mode: 'Add' | 'Edit';
  editAorData: AreaOfResponsibility | null;
}

export const AddUpdateAor: React.FC<AddUpdateAorProps> = ({
  show,
  onClose,
  mode,
  editAorData,
}) => {
  const [geoLocation, setGeoLocation] = useState<GeoLocation | undefined>(
    editAorData?.geolocation || undefined
  );
  const createAor = useCreateAor();

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onChange' });

  useEffect(() => {
    if (show && mode === 'Add') {
      reset({
        name: '',
        description: '',
      });
      setGeoLocation(undefined);
    }
  }, [show, mode, reset]);

  useEffect(() => {
    if (!!editAorData && mode === 'Edit') {
      setValue('name', editAorData.name);
      setValue('description', editAorData.description || '');
      setGeoLocation(editAorData.geolocation || undefined);
    }
  }, [editAorData, mode, setValue]);

  const onSubmit = (data: FormData) => {
    const current_location_geom =
      geoLocation?.geojson?.data?.features?.[0]?.geometry || null;
    const aor: AreaOfResponsibility = {
      id: editAorData?.id || generateUUID(),
      group_guid: editAorData?.group_guid || '',
      name: data.name,
      description: data.description,
      location: geoLocation ? JSON.stringify(geoLocation) : null,
      current_location_geom: current_location_geom
        ? JSON.stringify(current_location_geom)
        : null,
      category_id: null,
      parent_item_id: null,
      geolocation: geoLocation || null,
      cc_type: null,
    };
    createAor.mutate(aor);
    onClose();
  };
  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {`${mode === 'Edit' ? 'Update' : 'Add'} Area of Responsibility (AOR)`}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Name</label>
            <Controller
              control={control}
              name="name"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  placeholder="Enter Name"
                  type="text"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
            {errors?.name && (
              <span className="form-text form-error">
                This field is required
              </span>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Description</label>
            <Controller
              control={control}
              name="description"
              render={({ field: { onChange, value } }) => (
                <StylishNewTextArea onChange={onChange} value={value} />
              )}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Location:</label>
            <GeoLocationComponent
              location={geoLocation}
              setLocation={setGeoLocation}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              type="button"
              className="button--secondary button--reverse"
              onClick={onClose}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton type="submit" className="button--primary">
              {`${mode === 'Edit' ? 'Update' : 'Add'}` +
                ' Area of Responsibility (AOR)'}
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
